import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Upload,
  FileText,
  CheckCircle,
  AlertTriangle,
  Download,
  Users,
  Calendar,
  ClipboardList,
  Sparkles,
  Zap,
  Shield,
  Target,
  ArrowRight,
  FileCheck,
  Eye,
  RefreshCw,
  TrendingUp,
  Database,
  Filter,
  Check,
  X,
  AlertCircle,
  FileSpreadsheet,
  Send,
  Workflow,
  Brain,
  Layers,
  CheckCheck,
  Info,
  Clock,
  XCircle,
  Edit3,
  Search,
  Copy,
  Trash2,
  Link2,
  ArrowDown,
  List,
  Grid,
  Tag,
  UserPlus,
  Building,
  Briefcase,
  CalendarPlus,
  BarChart3,
  TrendingDown,
  Timer,
  MousePointer2,
  Lightbulb,
  ShieldCheck,
  Lock,
  FileWarning,
  Activity,
  Cpu,
  LayoutDashboard,
} from "lucide-react";
import HeaderMenu from "../component/Navbar/headermenu";
import CustomFooter from "../component/Footer/customFooter";
import "../../src/assets/css/Bulk.css";
import { Helmet } from "react-helmet";
import image1 from "../assets/new-images/client/01.jpg";
import image2 from "../assets/new-images/client/02.jpg";
import image3 from "../assets/new-images/client/03.jpg";
import image4 from "../assets/new-images/client/04.jpg";
import image5 from "../assets/new-images/client/05.jpg";

export default function BulkAction() {
  const [activeCategory, setActiveCategory] = useState("tasks");
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationStatus, setValidationStatus] = useState("idle");
  const [showConfetti, setShowConfetti] = useState(false);
  const [uploadedData, setUploadedData] = useState([]);
  const [aiThinking, setAiThinking] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [showSimilar, setShowSimilar] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [integrationView, setIntegrationView] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editedData, setEditedData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showFlash, setShowFlash] = useState(false);
  const [showRipple, setShowRipple] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [aiInsightIndex, setAiInsightIndex] = useState(0);
  const [templateHover, setTemplateHover] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Category configurations
  const categories = {
    clients: {
      icon: Users,
      color: "cyan",
      gradient: "from-cyan-500 to-blue-500",
      label: "Bulk Client Upload",
      description:
        "Import multiple clients at once with intelligent data mapping",
      recordCount: 156,
      maxCapacity: "1,000 clients",
      avgTime: "30 seconds",
    },
    tasks: {
      icon: ClipboardList,
      color: "emerald",
      gradient: "from-emerald-500 to-green-500",
      label: "Bulk Task Upload",
      description:
        "Upload hundreds of tasks instantly with auto-categorization",
      recordCount: 243,
      maxCapacity: "1,000 tasks",
      avgTime: "30 seconds",
    },
    events: {
      icon: Calendar,
      color: "purple",
      gradient: "from-purple-500 to-violet-500",
      label: "Bulk Event Upload",
      description: "Schedule multiple events seamlessly with smart validation",
      recordCount: 89,
      maxCapacity: "1,000 events",
      avgTime: "30 seconds",
    },
  };

  const currentCategory = categories[activeCategory];

  // Sample data templates
  const sampleDataTemplates = {
    tasks: [
      {
        taskName: "Q4 Financial Report",
        project: "Finance 2024",
        client: "Acme Corp",
        fy: "FY 2024-25",
        period: "Quarter 4",
        assignee: "john@example.com",
        dueDate: "15-12-2025",
      },
      {
        taskName: "Client Onboarding Documentation",
        project: "Sales Pipeline",
        client: "TechStart Inc",
        fy: "FY 2024-25",
        period: "January",
        assignee: "sarah@example.com",
        dueDate: "20-01-2026",
      },
      {
        taskName: "Annual Budget Planning",
        project: "Operations",
        client: "Global Industries",
        fy: "FY 2025-26",
        period: "Annual",
        assignee: "mike@example.com",
        dueDate: "01-02-2026",
      },
      {
        taskName: "Q4 Financial Report",
        project: "Finance 2024",
        client: "Acme Corp",
        fy: "FY 2024-25",
        period: "Quarter 4",
        assignee: "john@example.com",
        dueDate: "15-12-2025",
      },
      {
        taskName: "Marketing Campaign Review",
        project: "Marketing 2025",
        client: "StartupCo",
        fy: "FY 2024-25",
        period: "Quarter 1",
        assignee: "invalidteam@",
        dueDate: "10-01-2026",
      },
    ],
    clients: [
      {
        companyName: "Acme Corporation",
        contactPerson: "John Smith",
        email: "john@acme.com",
        phone: "+1-555-0123",
        country: "US",
        industry: "Technology",
      },
      {
        companyName: "TechStart Inc",
        contactPerson: "Sarah Johnson",
        email: "sarah@techstart.com",
        phone: "+1-555-0456",
        country: "CA",
        industry: "Software",
      },
      {
        companyName: "Global Industries",
        contactPerson: "Mike Chen",
        email: "mike@global.com",
        phone: "+1-555-0789",
        country: "US",
        industry: "Manufacturing",
      },
      {
        companyName: "TechStart International",
        contactPerson: "Bob Williams",
        email: "bob@techstart-intl.com",
        phone: "+1-555-0999",
        country: "UK",
        industry: "Software",
      },
    ],
    events: [
      {
        title: "Quarterly Review Meeting",
        startDate: "2025-03-15 10:00",
        endDate: "2025-03-15 12:00",
        type: "Meeting",
        attendees: "john@example.com, sarah@example.com",
        location: "Conference Room A",
      },
      {
        title: "Project Deadline",
        startDate: "2025-03-20 17:00",
        endDate: "2025-03-20 17:00",
        type: "Deadline",
        attendees: "team@example.com",
        location: "Virtual",
      },
      {
        title: "Client Presentation",
        startDate: "2025-03-25 14:00",
        endDate: "2025-03-25 16:00",
        type: "Meeting",
        attendees: "invalidteam@",
        location: "Client Office",
      },
    ],
  };

  // Guidelines data
  const guidelines = {
    clients: [
      {
        text: "Use the official template (don't change headers)",
        icon: FileCheck,
      },
      {
        text: "Email format: valid email addresses required",
        icon: AlertCircle,
      },
      {
        text: "Phone: +1-xxx-xxx-xxxx or (xxx) xxx-xxxx formats",
        icon: AlertCircle,
      },
      {
        text: "Required fields: Company Name, Contact Person, Email",
        icon: AlertTriangle,
      },
      {
        text: "Country codes: Use ISO 2-letter codes (US, CA, UK)",
        icon: Info,
      },
      {
        text: "Duplicate check: System auto-detects existing clients",
        icon: Shield,
      },
      { text: "Save as .csv or .xlsx and upload", icon: Upload },
    ],
    tasks: [
      {
        text: "Use the official template (don't change headers)",
        icon: FileCheck,
      },
      { text: "Date: dd-mm-yyyy (e.g., 09-11-2025)", icon: Calendar },
      {
        text: "Required: Project, Task, Client, FY, Period, Assignee",
        icon: AlertTriangle,
      },
      {
        text: "Assignee: email only, must already exist; names create errors",
        icon: UserPlus,
      },
      {
        text: "FY: format FY 2024â€“25; if missing/inactive, add or activate during upload",
        icon: Tag,
      },
      {
        text: "Period: only template values (e.g., January, Quarter 1, Annual)",
        icon: Clock,
      },
      { text: "Save as .csv and upload", icon: Upload },
    ],
    events: [
      {
        text: "Use the official template (don't change headers)",
        icon: FileCheck,
      },
      { text: "DateTime: yyyy-mm-dd HH:mm format", icon: Clock },
      {
        text: "Required: Title, Start Date, End Date, Type",
        icon: AlertTriangle,
      },
      {
        text: "Event Type: Meeting, Deadline, Reminder, or Milestone",
        icon: Tag,
      },
      { text: "Attendees: comma-separated email addresses", icon: Users },
      { text: "Duration: Auto-calculated from start/end times", icon: Timer },
      { text: "Save as .csv or .xlsx and upload", icon: Upload },
    ],
  };

  const ImageData = {
    img1: image1,
    img2: image2,
    img3: image3,
    img4: image4,
    img5: image5,
  };
  const currentGuidelines = guidelines[activeCategory];

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateData = (data, category) => {
    const validatedData = [];
    const seenItems = new Map();

    data.forEach((item, index) => {
      const row = {
        id: index + 1,
        status: "valid",
        ...item,
      };

      // Category-specific validation
      if (category === "tasks") {
        // Check for duplicate tasks
        const taskKey = `${item.taskName}-${item.project}-${item.client}`;
        if (seenItems.has(taskKey)) {
          row.status = "duplicate";
          row.duplicateOf = `Task #${seenItems.get(taskKey)}`;
        } else {
          seenItems.set(taskKey, row.id);
        }

        // Check for invalid email
        if (item.assignee && !validateEmail(item.assignee)) {
          row.status = "error";
          row.error = "Invalid assignee email format";
        }

        // Mark new items
        row.isNew = {
          fy: item.fy === "FY 2025-26",
          period: false,
          client: item.client === "TechStart Inc",
          assignee: false,
        };

        // Set badge colors
        row.badges = {
          project: "pink",
          fy: "green",
          period: "blue",
          client: "yellow",
        };
      } else if (category === "clients") {
        // Check for similar clients
        const similarClient = validatedData.find(
          (existing) =>
            existing.companyName &&
            item.companyName &&
            existing.companyName
              .toLowerCase()
              .includes(item.companyName.split(" ")[0].toLowerCase()),
        );

        if (similarClient && similarClient.companyName !== item.companyName) {
          row.status = "similar";
          row.similarTo = similarClient.companyName;
        }

        // Validate email
        if (item.email && !validateEmail(item.email)) {
          row.status = "error";
          row.error = "Invalid email format";
        }

        row.isNew = false;
      } else if (category === "events") {
        // Validate attendees
        if (item.attendees) {
          const emails = item.attendees.split(",").map((e) => e.trim());
          const invalidEmail = emails.find((email) => !validateEmail(email));
          if (invalidEmail) {
            row.status = "error";
            row.error = `Invalid attendee email: ${invalidEmail}`;
          }
        }
      }

      validatedData.push(row);
    });

    return validatedData;
  };

  // Calculate validation stats
  const getValidationStats = () => {
    const totalUploaded = uploadedData.length;
    const validated = uploadedData.filter(
      (row) => row.status === "valid",
    ).length;
    const duplicates = uploadedData.filter(
      (row) => row.status === "duplicate",
    ).length;
    const similar = uploadedData.filter(
      (row) => row.status === "similar",
    ).length;
    const errors = uploadedData.filter((row) => row.status === "error").length;

    return { totalUploaded, validated, duplicates, similar, errors };
  };

  const validationStats = getValidationStats();

  // Handle file upload
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const processFile = (file) => {
    setUploadedFile(file);
    setUploadProgress(0);

    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            // Use sample data for demo
            const sampleData = sampleDataTemplates[activeCategory];
            const validated = validateData(sampleData, activeCategory);
            setUploadedData(validated);
            setCurrentStep(2);
            setValidationStatus("validating");
            setAiThinking(true);

            // Simulate AI validation
            setTimeout(() => {
              setAiThinking(false);
              setValidationStatus("complete");
            }, 3000);
          }, 500);
          return 100;
        }
        return prev + 10;
      });
    }, 100);
  };

  // Handle demo upload (use sample data)
  const handleDemoUpload = () => {
    const demoFile = new File([""], `sample_${activeCategory}.csv`, {
      type: "text/csv",
    });
    processFile(demoFile);
  };

  // Download template
  const downloadTemplate = () => {
    let csvContent = "";

    if (activeCategory === "tasks") {
      csvContent = "Task Name,Project,Client,FY,Period,Assignee,Due Date\n";
      csvContent +=
        "Sample Task,Sample Project,Sample Client,FY 2024-25,Quarter 1,user@example.com,15-01-2026\n";
    } else if (activeCategory === "clients") {
      csvContent = "Company Name,Contact Person,Email,Phone,Country,Industry\n";
      csvContent +=
        "Sample Corp,John Doe,john@sample.com,+1-555-0123,US,Technology\n";
    } else if (activeCategory === "events") {
      csvContent = "Title,Start Date,End Date,Type,Attendees,Location\n";
      csvContent +=
        "Sample Meeting,2025-03-15 10:00,2025-03-15 12:00,Meeting,user@example.com,Office\n";
    }

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeCategory}_template.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Edit row
  const startEditingRow = (row) => {
    setEditingRow(row.id);
    setEditedData({ ...row });
  };

  const saveEditedRow = () => {
    if (editedData) {
      setUploadedData((prev) =>
        prev.map((row) =>
          row.id === editedData.id ? { ...editedData, status: "valid" } : row,
        ),
      );
      setEditingRow(null);
      setEditedData(null);
    }
  };

  const cancelEditing = () => {
    setEditingRow(null);
    setEditedData(null);
  };

  // Delete row
  const deleteRow = (id) => {
    setUploadedData((prev) => prev.filter((row) => row.id !== id));
  };

  // Mark duplicate as keep/remove
  const handleDuplicateAction = (id, action) => {
    if (action === "remove") {
      deleteRow(id);
    } else {
      setUploadedData((prev) =>
        prev.map((row) => (row.id === id ? { ...row, status: "valid" } : row)),
      );
    }
  };

  // Handle similar match
  const handleSimilarMatch = (id, useExisting) => {
    if (useExisting) {
      deleteRow(id);
    } else {
      setUploadedData((prev) =>
        prev.map((row) => (row.id === id ? { ...row, status: "valid" } : row)),
      );
    }
  };

  // Reset when category changes
  useEffect(() => {
    setCurrentStep(1);
    setUploadedFile(null);
    setUploadProgress(0);
    setValidationStatus("idle");
    setUploadedData([]);
    setAiThinking(false);
    setShowDuplicates(false);
    setShowSimilar(false);
    setShowErrors(false);
    setIntegrationView(false);
    setEditingRow(null);
    setEditedData(null);
  }, [activeCategory]);

  // Scroll progress tracker
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Rotating AI insights
  useEffect(() => {
    if (aiThinking) {
      const insights = [
        "Checking for duplicates...",
        "Validating formats...",
        "Analyzing similarities...",
        "Cross-referencing data...",
      ];
      const interval = setInterval(() => {
        setAiInsightIndex((prev) => (prev + 1) % insights.length);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [aiThinking]);

  const handleSubmit = () => {
    // Cinematic flash effect
    setShowFlash(true);
    setTimeout(() => setShowFlash(false), 200);

    // Ripple wave effect
    setShowRipple(true);
    setTimeout(() => setShowRipple(false), 2000);

    setIsSubmitting(true);

    // Simulate submit processing
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setShowConfetti(true);

      // Scroll to integration view
      setTimeout(() => {
        setIntegrationView(true);
        // Smooth scroll to the integration section
        const integrationSection = document.getElementById(
          "integration-section",
        );
        if (integrationSection) {
          integrationSection.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 500);

      setTimeout(() => {
        setShowConfetti(false);
      }, 3000);

      // Auto-hide success toast after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    }, 2000);
  };

  const handleReset = () => {
    setCurrentStep(1);
    setUploadedFile(null);
    setUploadProgress(0);
    setValidationStatus("idle");
    setUploadedData([]);
    setIntegrationView(false);
    setIsSubmitting(false);
    setSubmitSuccess(false);

    // Scroll back to demo section
    const demoSection = document.getElementById("demo-section");
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <Helmet>
        <title>Bulk Actions. Impossibly Fast. | Simplifyte</title>
      </Helmet>
      <div className="min-h-screen bg-white relative">
        {/* Scroll Progress Indicator */}
        <motion.div
          className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#093a5b] via-[#1d6fa4] to-[#0ea5e9] z-[10002] origin-left"
          style={{ scaleX: scrollProgress / 100 }}
        />

        {/* Cinematic Flash Effect */}
        <AnimatePresence>
          {showFlash && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.8, 0] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-white z-[10001] pointer-events-none"
            />
          )}
        </AnimatePresence>
        {/* Hero Section - Theatrical Reveal */}
        <section className="bulkaction_Main">
          {/* Dot Pattern Background */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: "50px 50px",
            }}
          />
          <HeaderMenu />

          {/* Dynamic Orbs */}
          {/* <motion.div
            key={`orb-1-${activeCategory}`}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5 }}
            className="absolute top-20 right-1/4 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px]"
          /> */}
          {/* <motion.div
            key={`orb-2-${activeCategory}`}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5, delay: 0.2 }}
            className="absolute bottom-20 left-1/4 w-[500px] h-[500px] bg-emerald-400/10 rounded-full blur-[100px]"
          /> */}

          {/* Spotlight Effect */}
          {/* <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(6,182,212,0.05),transparent_50%)]" /> */}

          {/* Floating Particles */}
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-cyan-400/30 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.3, 0.8, 0.3],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}

          <div className="max-w-7xl mx-auto relative z-10 w-full">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="text-center mb-20"
            >
              {/* Badge */}
              {/* <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="bulkaction_sfBadge__pill"
              >
                {/* inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border-2 border-cyan-500/30 text-cyan-400 px-8 py-3 rounded-full  */}
              {/* mb-12 backdrop-blur-sm shadow-lg 
                <Layers className="w-5 h-5" />
                <span className="tracking-wide Bulk_text_res">
                  AI-Enhanced Bulk Operations
                </span>
              </motion.div> */}

              {/* Main Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bulkaction_Font_class"
              >
                Bulk Actions.
                <br />
                {/* bg-gradient-to-r from-cyan-400 via-emerald-400 to-purple-400 bg-clip-text text-transparent */}
                <span className="text-[#093a5b]">Impossibly Fast.</span>
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="bulkaction_Para"
              >
                AI-enhanced bulk uploads auto-classify and validate data for
                seamless integration.
              </motion.p>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="bulkaction_counter"
              >
                <motion.div
                  className="text-center"
                  whileHover={{ scale: 1.05 }}
                >
                  <motion.div className="bulkaction_subcounter_class">
                    10,000+
                    {/* <motion.div
                      className="absolute inset-0 bg-cyan-500/20 blur-xl rounded-full"
                      animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    /> */}
                  </motion.div>
                  <div className="text-sm text-gray-500 tracking-wide">
                    Records Processed Daily
                  </div>
                </motion.div>
                <div className="hidden w-px h-16 bg-gradient-to-b from-transparent via-white/30 to-transparent" />
                <motion.div
                  className="text-center"
                  whileHover={{ scale: 1.05 }}
                >
                  <motion.div className="bulkaction_subcounter_class">
                    99.9%
                    {/* <motion.div
                      className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full"
                      animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                      transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                    /> */}
                  </motion.div>
                  <div className="text-sm text-gray-500 tracking-wide">
                    Validation Accuracy
                  </div>
                </motion.div>
                <div className="hidden w-px h-16 bg-gradient-to-b from-transparent via-white/30 to-transparent" />
                <motion.div
                  className="text-center"
                  whileHover={{ scale: 1.05 }}
                >
                  <motion.div className="bulkaction_subcounter_class">
                    30 sec
                    {/* <motion.div
                      className="absolute inset-0 bg-emerald-500/20 blur-xl rounded-full"
                      animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                      transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                    /> */}
                  </motion.div>
                  <div className="text-sm text-gray-500 tracking-wide">
                    Average Processing Time
                  </div>
                </motion.div>
              </motion.div>

              {/* Data Flow Animation */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0 }}
                className="bulkaction_dataflow"
              >
                <motion.div
                  className="flex items-center gap-3  backdrop-blur-sm border-2 border-[#f2f2f2] px-6 py-3 rounded-full group"
                  whileHover={{
                    scale: 1.05,
                    borderColor: "#093a5b",
                    backgroundColor: "#093a5b",
                  }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }} // ðŸ‘ˆ smooth motion
                >
                  <FileSpreadsheet className="w-5 h-5 text-cyan-400 group-hover:text-white" />
                  <span className="text-sm text-[#093a5b] group-hover:text-white">
                    CSV Upload
                  </span>
                </motion.div>

                <motion.div
                  animate={{ x: [0, 10, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <ArrowRight className="w-5 h-5 text-gray-500" />
                </motion.div>

                <motion.div
                  className="flex items-center gap-3  backdrop-blur-sm border-2 border-[#f2f2f2] px-6 py-3 rounded-full group transition-colors duration-300"
                  whileHover={{
                    scale: 1.05,
                    borderColor: "#093a5b",
                    backgroundColor: "#093a5b",
                  }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                >
                  <Brain className="w-5 h-5 text-purple-400 transition-colors duration-300 group-hover:text-white" />
                  <span className="text-sm text-[#093a5b] transition-colors duration-300 group-hover:text-white">
                    AI Validation
                  </span>
                </motion.div>

                <motion.div
                  animate={{ x: [0, 10, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
                >
                  <ArrowRight className="w-5 h-5 text-gray-500" />
                </motion.div>

                <motion.div
                  className="flex items-center gap-3 backdrop-blur-sm border-2 border-[#f2f2f2] px-6 py-3 rounded-full group"
                  whileHover={{
                    scale: 1.05,
                    borderColor: "#093a5b",
                    backgroundColor: "#093a5b",
                  }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                >
                  <Database className="w-5 h-5 text-emerald-400 group-hover:text-white" />
                  <span className="text-sm text-[#093a5b] group-hover:text-white">
                    Instant Import
                  </span>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>

          {/* Scroll Indicator */}
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
          >
            <div className="flex flex-col items-center gap-2 text-gray-500">
              <span className="text-sm">Scroll to explore</span>
              <ArrowDown className="w-5 h-5" />
            </div>
          </motion.div>
        </section>

        {/* The Problem Section */}
        <section className="bulkaction_Manual_data_Main_Class">
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-20-responsive"
            >
              <h2 className="bulkaction_Manual_data_para">
                Manual Data Entry is{" "}
                <span className="text-red-600">Killing Productivity</span>
              </h2>
              <p className=" bulkaction_Manual_data_subpara">
                Teams waste countless hours entering data one record at a time.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-12">
              {/* Manual Way */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="bulkaction_oldway_Class"
              >
                {/* Animated Background Glow */}
                <motion.div
                  animate={{
                    opacity: [0.1, 0.2, 0.1],
                    scale: [1, 1.2, 1],
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-transparent blur-3xl"
                />

                {/* Floating Particles */}
                {[...Array(8)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-1 h-1 bg-red-400/30 rounded-full"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                    }}
                    animate={{
                      y: [0, -20, 0],
                      opacity: [0.2, 0.5, 0.2],
                    }}
                    transition={{
                      duration: 3 + Math.random() * 2,
                      repeat: Infinity,
                      delay: Math.random() * 2,
                    }}
                  />
                ))}
                <div className="bulkaction_card">
                  <motion.div
                    className="bulkaction_card_Icon"
                    whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    {/* Icon Pulsing Glow */}
                    <motion.div
                      animate={{
                        scale: [1, 1.5, 1],
                        opacity: [0.3, 0.6, 0.3],
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="absolute inset-0 bg-red-500/30 rounded-2xl blur-lg"
                    />
                    <TrendingDown className="w-8 h-8 text-red-400 relative z-10" />
                  </motion.div>
                  <motion.h3
                    className="text-2xl text-gray-900"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    The Old Way
                  </motion.h3>
                </div>

                <ul className="space-y-4 relative z-10">
                  {[
                    "Enter 200 tasks manually, one by one",
                    "Takes 3-4 hours of repetitive work",
                    "High risk of typos and errors",
                    "No duplicate detection",
                    "Missing data validation",
                    "Team members waiting for data",
                  ].map((item, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        delay: idx * 0.1,
                        type: "spring",
                        stiffness: 100,
                      }}
                      whileHover={{ x: 5, scale: 1.02 }}
                      className="flex items-start gap-3 text-gray-700"
                    >
                      <motion.div
                        animate={{ rotate: [0, 360] }}
                        transition={{
                          duration: 20,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      >
                        <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      </motion.div>
                      <span>{item}</span>
                    </motion.li>
                  ))}
                </ul>

                <motion.div
                  className="bulkaction_card_Time"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 }}
                  whileHover={{ scale: 1.05 }}
                >
                  {/* Scanning Line Effect */}
                  <motion.div
                    animate={{ y: ["0%", "100%"] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    className="absolute inset-0 h-1/3 bg-gradient-to-b from-transparent via-red-400/10 to-transparent"
                  />

                  <div className="flex items-center gap-3 mb-2 relative z-10">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      <Clock className="w-5 h-5 text-red-400" />
                    </motion.div>
                    <span className="text-gray-900 font-medium">
                      Time Wasted
                    </span>
                  </div>
                  <motion.div
                    className="bulkaction_card_Hours"
                    style={{ fontWeight: 200 }}
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    3-4 hours
                  </motion.div>
                  <div className="text-sm text-gray-600 mt-1 relative z-10">
                    Per 200 records
                  </div>
                </motion.div>
              </motion.div>

              {/* Bulk Way */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="bulkaction_simplifyte_card"
              >
                {/* Animated Background Glow */}
                <motion.div
                  animate={{
                    opacity: [0.1, 0.3, 0.1],
                    scale: [1, 1.2, 1],
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 via-cyan-500/20 to-transparent blur-3xl"
                />

                {/* Success Sparkles */}
                {[...Array(12)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-1 h-1 bg-emerald-400/40 rounded-full"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                    }}
                    animate={{
                      y: [0, -30, 0],
                      opacity: [0.3, 1, 0.3],
                      scale: [0, 1.5, 0],
                    }}
                    transition={{
                      duration: 2 + Math.random() * 2,
                      repeat: Infinity,
                      delay: Math.random() * 2,
                    }}
                  />
                ))}
                <div className="bulkaction_card">
                  <motion.div
                    className=" bulkaction_simplifyte_card_Icon"
                    whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    {/* Icon Pulsing Glow */}
                    <motion.div
                      animate={{
                        scale: [1, 1.5, 1],
                        opacity: [0.4, 0.8, 0.4],
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="absolute inset-0 bg-emerald-500/40 rounded-2xl blur-lg"
                    />
                    <motion.div
                      animate={{ y: [0, -5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <TrendingUp className="w-8 h-8 text-[#1d6fa4] relative z-10" />
                    </motion.div>
                  </motion.div>
                  <motion.h3
                    className="text-2xl text-gray-900"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    The    Way
                  </motion.h3>
                </div>

                <ul className="space-y-4 relative z-10">
                  {[
                    "Upload CSV with 1,000 tasks instantly",
                    "Complete in just 30 seconds",
                    "AI validates every field automatically",
                    "Smart duplicate detection",
                    "Real-time error highlighting",
                    "Team has data immediately",
                  ].map((item, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        delay: idx * 0.1,
                        type: "spring",
                        stiffness: 100,
                      }}
                      whileHover={{ x: -5, scale: 1.02 }}
                      className="flex items-start gap-3 text-gray-700"
                    >
                      <motion.div
                        animate={{
                          scale: [1, 1.2, 1],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: idx * 0.2,
                        }}
                      >
                        <CheckCircle className="w-5 h-5 text-[#1d6fa4] flex-shrink-0 mt-0.5" />
                      </motion.div>
                      <span>{item}</span>
                    </motion.li>
                  ))}
                </ul>

                <motion.div
                  className=" bulkaction_simplifyte_Time"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 }}
                  whileHover={{ scale: 1.05 }}
                >
                  {/* Energy Flow Effect */}
                  <motion.div
                    animate={{ x: ["-100%", "200%"] }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/10 to-transparent"
                  />

                  <div className="flex items-center gap-3 mb-2 relative z-10">
                    <motion.div
                      animate={{
                        rotate: [0, 360],
                        scale: [1, 1.2, 1],
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Zap className="w-5 h-5 text-[#1d6fa4]" />
                    </motion.div>
                    <span className="text-gray-900 font-medium">
                      Time Saved
                    </span>
                  </div>
                  <motion.div
                    className="bulkaction_simplifyte_Time_Font_Class"
                    style={{ fontWeight: 200 }}
                    animate={{
                      scale: [1, 1.05, 1],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    30 seconds
                  </motion.div>
                  <div className="text-sm text-gray-600 mt-1 relative z-10">
                    Per 1,000 records
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Template Education Section */}
        {/* bg-gradient-to-b from-gray-700 via-gray-600 to-gray-700  */}
        <section className="relative bg-white py-32 px-6 overflow-hidden">
          {/* <div className="absolute inset-0 bg-gradient-to-b from-gray-700 via-gray-600 to-gray-700" /> */}

          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-20"
            >
              {/* <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-purple-400/40 text-purple-300 px-6 py-2 rounded-full mb-8">
                <FileSpreadsheet className="w-4 h-4" />
                <span className="text-sm">Template Preparation</span>
              </div> */}
              <h2 className="Bulk_Template_Preparation">
                Prepare Your Data{" "}
                <span
                  // bg-gradient-to-r from-purple-300 to-cyan-300 bg-clip-text text-transparent
                  className="text-[#093a5b]"
                  style={{ fontWeight: 700 }}
                >
                  In 3 Minutes
                </span>
              </h2>
              <p className="Bulk_Template_para">
                Download our template, add your data, and you're ready to
                upload.
              </p>
            </motion.div>

            {/* Template Cards */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {["clients", "tasks", "events"].map((category, idx) => {
                const config = categories[category];
                const Icon = config.icon;

                const templatePreview = {
                  clients: [
                    "Company Name*",
                    "Contact Person*",
                    "Email*",
                    "Phone",
                    "Country",
                  ],
                  tasks: [
                    "Project*",
                    "Task*",
                    "Client*",
                    "FY*",
                    "Period*",
                    "Assignee*",
                  ],
                  events: [
                    "Title*",
                    "Start Date*",
                    "End Date*",
                    "Type*",
                    "Attendees",
                  ],
                };

                return (
                  <motion.div
                    key={category}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    whileHover={{ y: -10 }}
                    onHoverStart={() => setTemplateHover(category)}
                    onHoverEnd={() => setTemplateHover(null)}
                    className="bg-gradient-to-br from-white/[0.15] to-white/[0.08] backdrop-blur-xl border-2 border-[#f2f2f2]
                     rounded-2xl p-8 hover:border-white/30 transition-all relative overflow-hidden
                      shadow-[0_4px_6px_-1px_rgba(9,58,91,0.1)] hover:shadow-[0_20px_25px_-5px_rgba(9,58,91,0.2)] transition-shadow duration-300"
                  >
                    {/* Template Preview Overlay */}
                    <AnimatePresence>
                      {templateHover === category && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 20 }}
                          className="absolute inset-0 bg-gray-900/95 backdrop-blur-xl z-10 p-8 flex flex-col justify-center"
                        >
                          <div className="text-xs text-cyan-400 mb-3">
                            TEMPLATE PREVIEW
                          </div>
                          <div className="space-y-2">
                            {templatePreview[category].map((field, i) => (
                              <motion.div
                                key={field}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.05 }}
                                className="flex items-center gap-2 text-sm text-gray-300 bg-white/5 px-3 py-2 rounded-lg"
                              >
                                <div className="w-1 h-1 bg-cyan-400 rounded-full" />
                                {field}
                              </motion.div>
                            ))}
                          </div>
                          <div className="mt-4 text-xs text-gray-400">
                            Hover out to return
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div
                      className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center mb-6`}
                    >
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3
                      className={`text-2xl  mb-3 ${templateHover === category ? "font-normal text-gray-100" : "font-semibold text-[#093a5b]"}`}
                    >
                      {category.charAt(0).toUpperCase() + category.slice(1)}{" "}
                      Template
                    </h3>
                    <p className="text-[#94A3B8] mb-6">
                      Pre-formatted CSV template with all required fields and
                      validation rules.
                    </p>

                    <div className="mb-4 flex items-center gap-2 text-xs text-gray-300">
                      <Eye className="w-3 h-3" />
                      <span className="text-[#94a3b8]">
                        Hover to preview template fields
                      </span>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={downloadTemplate}
                      className={`w-full px-6 py-3 bg-gradient-to-r ${config.gradient} text-white rounded-xl shadow-lg hover:shadow-2xl hover:shadow-${config.color}-500/50 transition-all flex items-center justify-center gap-2`}
                    >
                      <Download className="w-4 h-4" />
                      <span>Download CSV</span>
                    </motion.button>
                  </motion.div>
                );
              })}
            </div>

            {/* Guidelines Preview */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="Bulk_Quick_guide"
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="p-4 bg-cyan-500/20 rounded-2xl">
                  <Lightbulb className="w-8 h-8 text-cyan-300" />
                </div>
                <div>
                  <h3 className="text-2xl text-white mb-1">Quick Guidelines</h3>
                  <p className="text-gray-200">
                    Follow these rules for successful uploads
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    icon: FileCheck,
                    text: "Always use the official template - never modify headers",
                  },
                  {
                    icon: AlertCircle,
                    text: "Fill all required fields (marked with *)",
                  },
                  {
                    icon: Calendar,
                    text: "Use correct date formats (dd-mm-yyyy)",
                  },
                  {
                    icon: Shield,
                    text: "AI will auto-detect duplicates during validation",
                  },
                ].map((guideline, idx) => {
                  const Icon = guideline.icon;
                  return (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-start gap-4 p-4 bg-white/10 rounded-xl group hover:bg-white/15 transition-all relative overflow-hidden"
                    >
                      {/* Checkmark Animation */}
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        whileInView={{ scale: 1, rotate: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          delay: idx * 0.1 + 0.3,
                          type: "spring",
                          stiffness: 200,
                        }}
                        className="absolute -right-2 -top-2 w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Check className="w-6 h-6 text-emerald-400" />
                      </motion.div>

                      <motion.div
                        initial={{ scale: 0.8 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.1 + 0.2 }}
                      >
                        <Icon className="w-6 h-6 text-cyan-300 flex-shrink-0 mt-1" />
                      </motion.div>
                      <span className="text-gray-100">{guideline.text}</span>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Main Interactive Demo Section */}
        <section
          id="demo-section"
          className="Bulk_Interactive_Demo bg-white"
        >
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-14"
            >
              <h2 className="Interactive_Demo_Heading">
                See It{" "}
                <span className="text-[#093a5b]">In Action</span>
              </h2>
              <p className="Interactive_Demo_Para">
                Experience the complete upload flow â€” just like the real product.
              </p>
            </motion.div>

            {/* â”€â”€ Product-faithful Bulk Upload Demo â”€â”€ */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="w-full"
            >
              {/* Category selector pills */}
              <div className="flex items-center gap-3 mb-8 flex-wrap">
                {Object.keys(categories).map((category) => {
                  const config = categories[category];
                  const Icon = config.icon;
                  const isActive = activeCategory === category;
                  return (
                    <button
                      key={category}
                      onClick={() => { setActiveCategory(category); handleReset(); }}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium border transition-all ${
                        isActive
                          ? "bg-[#093a5b] text-white border-[#093a5b]"
                          : "bg-white text-gray-600 border-gray-300 hover:border-[#093a5b] hover:text-[#093a5b]"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </button>
                  );
                })}
              </div>

              {/* Main product-faithful container */}
              <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">

                {/* Step header bar â€” matches product exactly */}
                <div className="border-b border-gray-200 px-8 py-4">
                  <div className="flex items-center gap-0">
                    {[
                      { num: 1, label: "Upload" },
                      { num: 2, label: "Review" },
                      { num: 3, label: "Submit" },
                    ].map((step, idx) => {
                      const isActive = currentStep === step.num;
                      const isCompleted = currentStep > step.num;
                      return (
                        <div key={step.num} className="flex items-center">
                          <div className={`px-6 py-2 border-b-2 transition-all ${
                            isActive ? "border-[#093a5b]" : isCompleted ? "border-gray-300" : "border-transparent"
                          }`}>
                            <div className={`text-sm font-semibold ${isActive ? "text-[#093a5b]" : "text-gray-400"}`}>
                              Step {step.num}
                            </div>
                            <div className={`text-xs ${isActive ? "text-[#093a5b]" : "text-gray-400"}`}>
                              {step.label}
                            </div>
                          </div>
                          {idx < 2 && <div className="w-px h-10 bg-gray-200 mx-1" />}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Step content */}
                <AnimatePresence mode="wait">

                  {/* â”€â”€ STEP 1: Upload â”€â”€ */}
                  {currentStep === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      {/* Sub-header */}
                      <div className="flex items-center justify-between px-8 py-4 border-b border-gray-100">
                        <h3 className="text-base font-semibold text-[#093a5b]">
                          Upload the CSV/Excel Template
                        </h3>
                        {uploadedFile && uploadProgress === 100 && (
                          <motion.button
                            whileTap={{ scale: 0.97 }}
                            onClick={() => setCurrentStep(2)}
                            className="px-4 py-1.5 border border-[#093a5b] text-[#093a5b] text-sm rounded hover:bg-[#093a5b] hover:text-white transition-all"
                          >
                            Proceed to Step 2
                          </motion.button>
                        )}
                        {!uploadedFile && (
                          <span className="text-sm text-gray-400 border border-gray-300 rounded px-3 py-1">
                            Please Upload File
                          </span>
                        )}
                      </div>

                      {/* Two-column layout */}
                      <div className="grid md:grid-cols-2 divide-x divide-gray-100">

                        {/* Left: Upload area */}
                        <div className="p-8">
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx"
                            onChange={handleFileSelect}
                            className="hidden"
                          />

                          {!uploadedFile ? (
                            <div
                              onDrop={handleDrop}
                              onDragOver={handleDragOver}
                              onDragLeave={handleDragLeave}
                              onClick={() => fileInputRef.current?.click()}
                              className={`border-2 border-dashed rounded-xl p-16 text-center cursor-pointer transition-all ${
                                isDragging ? "border-[#1d6fa4] bg-blue-50" : "border-gray-300 hover:border-[#1d6fa4] hover:bg-gray-50"
                              }`}
                            >
                              <p className="text-gray-500 mb-6">
                                Upload your {activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} CSV File
                              </p>
                              <div className="flex items-center justify-center gap-3 flex-wrap">
                                <button
                                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                                  className="px-5 py-2 border border-gray-400 text-gray-700 rounded text-sm hover:border-[#093a5b] hover:text-[#093a5b] transition-all"
                                >
                                  Select File
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDemoUpload(); }}
                                  className="px-5 py-2 bg-[#093a5b] text-white rounded text-sm hover:bg-[#1d6fa4] transition-all"
                                >
                                  Try Demo Data
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="border-2 border-dashed border-gray-200 rounded-xl p-12 text-center bg-gray-50">
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center"
                              >
                                <CheckCircle className="w-7 h-7 text-emerald-600" />
                              </motion.div>
                              <p className="text-gray-700 font-medium mb-4">{uploadedFile.name}</p>

                              {uploadProgress < 100 ? (
                                <div className="space-y-2">
                                  {/* Progress dots like in product */}
                                  <div className="flex items-center justify-center gap-1.5">
                                    {[...Array(9)].map((_, i) => (
                                      <motion.div
                                        key={i}
                                        className="w-8 h-3 rounded-full"
                                        style={{ background: i < Math.ceil(uploadProgress / 11) ? "#093a5b" : "#e5e7eb" }}
                                        animate={{ opacity: i === Math.ceil(uploadProgress / 11) - 1 ? [0.5, 1] : 1 }}
                                        transition={{ duration: 0.5, repeat: Infinity }}
                                      />
                                    ))}
                                  </div>
                                  <p className="text-sm text-gray-500">{uploadProgress}%</p>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  <p className="text-emerald-600 text-sm font-medium">File Successfully Uploaded</p>
                                  <button
                                    onClick={() => { setUploadedFile(null); setUploadProgress(0); }}
                                    className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded hover:border-[#093a5b] transition-all"
                                  >
                                    Replace File
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Right: Template download */}
                        <div className="p-8 flex flex-col items-center justify-center text-center">
                          <p className="text-gray-500 text-sm mb-4">
                            Download the sample {activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} template to add your {activeCategory} details.
                          </p>
                          <button
                            onClick={downloadTemplate}
                            className="px-6 py-2 bg-[#093a5b] text-white text-sm rounded hover:bg-[#1d6fa4] transition-all flex items-center gap-2"
                          >
                            <Download className="w-4 h-4" />
                            CSV
                          </button>
                        </div>
                      </div>

                      {/* Guidelines */}
                      <div className="border-t border-gray-100 px-8 py-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-4">Guidelines :</h4>
                        <ol className="space-y-2">
                          {currentGuidelines.map((g, idx) => (
                            <li key={idx} className="text-sm text-gray-600">
                              {idx + 1}. {g.text}
                            </li>
                          ))}
                        </ol>
                      </div>
                    </motion.div>
                  )}

                  {/* â”€â”€ STEP 2: Review â”€â”€ */}
                  {currentStep === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      {/* Sub-header */}
                      <div className="flex items-center justify-between px-8 py-4 border-b border-gray-100">
                        <h3 className="text-base font-semibold text-[#093a5b]">
                          Review the {activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} Data Uploaded / Data Validation
                        </h3>
                        <div className="flex items-center gap-3">
                          {/* Progress dots */}
                          <div className="flex items-center gap-1">
                            {[...Array(3)].map((_, i) => (
                              <div key={i} className="w-8 h-2.5 rounded-full" style={{ background: i < 2 ? "#093a5b" : "#e5e7eb" }} />
                            ))}
                          </div>
                          {validationStatus === "complete" && (
                            <button
                              onClick={() => setCurrentStep(3)}
                              className="px-4 py-1.5 border border-gray-300 text-gray-700 text-sm rounded hover:border-[#093a5b] hover:text-[#093a5b] transition-all"
                            >
                              Next
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="px-8 py-6">
                        {validationStatus === "validating" ? (
                          <div className="text-center py-16">
                            <div className="flex items-center justify-center gap-1.5 mb-4">
                              {[...Array(9)].map((_, i) => (
                                <motion.div
                                  key={i}
                                  className="w-8 h-3 rounded-full bg-[#093a5b]"
                                  animate={{ opacity: [0.3, 1, 0.3] }}
                                  transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.13 }}
                                />
                              ))}
                            </div>
                            <p className="text-gray-500 text-sm">Validating your data...</p>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm text-gray-500 mb-4">
                              Please Review the {activeCategory} file you have uploaded. It consists of the following:
                            </p>

                            {/* Count chips */}
                            <div className="flex items-center gap-3 mb-6 flex-wrap">
                              <div className="border border-gray-300 rounded px-4 py-1.5 text-sm text-gray-700">
                                {activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} (Uploaded): <strong>{validationStats.totalUploaded}</strong>
                              </div>
                              <div className="border border-gray-300 rounded px-4 py-1.5 text-sm text-gray-700 flex items-center gap-1">
                                {activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} (After Reviewing): <strong className="ml-1">{validationStats.validated}</strong>
                                <Info className="w-3.5 h-3.5 text-gray-400 ml-1" />
                              </div>
                            </div>

                            {/* Duplicates */}
                            {validationStats.duplicates > 0 && (
                              <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-4">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                                    <span className="text-sm font-semibold text-gray-800">
                                      Duplicate {activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)}: {validationStats.duplicates}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => setShowDuplicates(!showDuplicates)}
                                      className="px-3 py-1 border border-gray-300 text-gray-600 text-sm rounded hover:border-[#093a5b] transition-all"
                                    >
                                      {showDuplicates ? "Hide" : "Review it"}
                                    </button>
                                    <button
                                      onClick={() => setShowDuplicates(false)}
                                      className="px-3 py-1 border border-gray-300 text-gray-600 text-sm rounded hover:border-[#093a5b] transition-all"
                                    >
                                      Skip
                                    </button>
                                  </div>
                                </div>
                                <p className="text-xs text-gray-500">
                                  There are {validationStats.duplicates} {activeCategory} which are identical to each other in the file you have uploaded. Click on the same to see the groups of duplicate {activeCategory}. You can choose to ignore the groups or merge them into one.
                                </p>
                                {showDuplicates && (
                                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="mt-4 space-y-2">
                                    {uploadedData.filter(r => r.status === "duplicate").map(row => (
                                      <div key={row.id} className="flex items-center justify-between p-3 bg-white border border-orange-200 rounded-lg">
                                        <div>
                                          <div className="text-sm font-medium text-gray-800">
                                            {activeCategory === "tasks" ? row.taskName : activeCategory === "clients" ? row.companyName : row.title}
                                          </div>
                                          <div className="text-xs text-gray-500">Duplicate of: {row.duplicateOf}</div>
                                        </div>
                                        <div className="flex gap-2">
                                          <button onClick={() => handleDuplicateAction(row.id, "keep")} className="px-3 py-1 border border-gray-300 text-gray-600 text-xs rounded hover:bg-emerald-50 hover:border-emerald-400 transition-all">Keep</button>
                                          <button onClick={() => handleDuplicateAction(row.id, "remove")} className="px-3 py-1 border border-red-300 text-red-600 text-xs rounded hover:bg-red-50 transition-all">Remove</button>
                                        </div>
                                      </div>
                                    ))}
                                  </motion.div>
                                )}
                              </div>
                            )}

                            {/* Similar */}
                            {validationStats.similar > 0 && (
                              <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-4">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-yellow-500" />
                                    <span className="text-sm font-semibold text-gray-800">
                                      Similar Existing {activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} : {validationStats.similar}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => setShowSimilar(!showSimilar)}
                                    className="px-3 py-1 border border-gray-300 text-gray-600 text-sm rounded hover:border-[#093a5b] transition-all"
                                  >
                                    {showSimilar ? "Hide" : "Review"}
                                  </button>
                                </div>
                                <p className="text-xs text-gray-500">
                                  There are {validationStats.similar} {activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} uploaded by you which already exist in the system. Please review the same. You can either review and delete them or continue with uploading the same.
                                </p>
                                {showSimilar && (
                                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="mt-4 space-y-2">
                                    {uploadedData.filter(r => r.status === "similar").map(row => (
                                      <div key={row.id} className="flex items-center justify-between p-3 bg-white border border-yellow-200 rounded-lg">
                                        <div className="flex items-center gap-3">
                                          <span className="text-sm text-gray-800">
                                            {activeCategory === "tasks" ? row.taskName : activeCategory === "clients" ? row.companyName : row.title}
                                          </span>
                                          <ArrowRight className="w-4 h-4 text-gray-400" />
                                          <span className="text-sm text-gray-500">Similar to: {row.similarTo}</span>
                                        </div>
                                        <div className="flex gap-2">
                                          <button onClick={() => handleSimilarMatch(row.id, true)} className="px-3 py-1 border border-gray-300 text-gray-600 text-xs rounded hover:border-[#093a5b] transition-all">Use Existing</button>
                                          <button onClick={() => handleSimilarMatch(row.id, false)} className="px-3 py-1 border border-gray-300 text-gray-600 text-xs rounded hover:border-emerald-400 transition-all">Keep New</button>
                                        </div>
                                      </div>
                                    ))}
                                  </motion.div>
                                )}
                              </div>
                            )}

                            {/* No issues */}
                            {validationStats.duplicates === 0 && validationStats.similar === 0 && validationStats.errors === 0 && (
                              <div className="text-center py-10">
                                <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
                                <p className="text-gray-600 font-medium">No {activeCategory} Found â€” All {validationStats.validated} records are valid!</p>
                              </div>
                            )}

                            {validationStats.duplicates === 0 && validationStats.similar > 0 && !showSimilar && (
                              <div className="text-center py-10 text-gray-400 text-sm">
                                No Similar {activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} Found in the Workspace
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {/* â”€â”€ STEP 3: Submit â”€â”€ */}
                  {currentStep === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      {/* Sub-header */}
                      <div className="flex items-center justify-between px-8 py-4 border-b border-gray-100">
                        <h3 className="text-base font-semibold text-[#093a5b]">Final Submit</h3>
                        {!isSubmitting && (
                          <div className="flex items-center gap-3">
                            {/* Progress dots â€” all filled */}
                            <div className="flex items-center gap-1">
                              {[...Array(3)].map((_, i) => (
                                <div key={i} className="w-8 h-2.5 rounded-full" style={{ background: i < 3 ? "#093a5b" : "#e5e7eb" }} />
                              ))}
                            </div>
                            <motion.button
                              whileTap={{ scale: 0.97 }}
                              onClick={handleSubmit}
                              className="px-4 py-1.5 border border-[#093a5b] text-[#093a5b] text-sm rounded hover:bg-[#093a5b] hover:text-white transition-all"
                            >
                              Submit
                            </motion.button>
                          </div>
                        )}
                        {isSubmitting && (
                          <div className="flex items-center gap-2">
                            <div className="w-40 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: "100%" }}
                                transition={{ duration: 2 }}
                                className="h-full bg-[#093a5b] rounded-full"
                              />
                            </div>
                            <span className="text-xs text-[#093a5b]">Uploading {activeCategory}: 0 / {validationStats.validated} - (0%)</span>
                          </div>
                        )}
                      </div>

                      <div className="px-8 py-6">
                        <p className="text-sm text-gray-500 mb-1">
                          Here is the {activeCategory} data you have reviewed from Step 1 to 3.
                        </p>
                        <p className="text-sm text-gray-500 mb-5">
                          Press the Submit button to add the {activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} in the System.
                        </p>

                        {/* Count chips */}
                        <div className="flex items-center gap-3 mb-6 flex-wrap">
                          <div className="border border-gray-300 rounded px-4 py-1.5 text-sm text-gray-700">
                            {activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} (Uploaded): <strong>{validationStats.totalUploaded}</strong>
                          </div>
                          <div className="border border-gray-300 rounded px-4 py-1.5 text-sm text-gray-700 flex items-center gap-1">
                            {activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} (After Reviewing): <strong className="ml-1">{validationStats.validated}</strong>
                            <Info className="w-3.5 h-3.5 text-gray-400 ml-1" />
                          </div>
                        </div>

                        {/* Confirmed data table */}
                        <div className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-gray-800">
                              Confirmed {activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)}
                            </h4>
                          </div>
                          <div className="overflow-x-auto">
                            {activeCategory === "tasks" && (
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-xs text-gray-400 border-b border-gray-100">
                                    <th className="px-5 py-3 text-left font-medium">Sr.No</th>
                                    <th className="px-5 py-3 text-left font-medium">Task Name</th>
                                    <th className="px-5 py-3 text-left font-medium">Project</th>
                                    <th className="px-5 py-3 text-left font-medium">Client</th>
                                    <th className="px-5 py-3 text-left font-medium">FY</th>
                                    <th className="px-5 py-3 text-left font-medium">Period</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {uploadedData.filter(r => r.status !== "duplicate").slice(0, 8).map((row, idx) => (
                                    <tr key={row.id} className="border-b border-gray-50 hover:bg-gray-50">
                                      <td className="px-5 py-3 text-gray-500">{idx + 1}</td>
                                      <td className="px-5 py-3">
                                        <span className="px-2 py-0.5 bg-pink-100 text-pink-700 rounded text-xs">{row.taskName}</span>
                                      </td>
                                      <td className="px-5 py-3 text-gray-600">{row.project}</td>
                                      <td className="px-5 py-3 text-gray-600">{row.client}</td>
                                      <td className="px-5 py-3 text-gray-600">{row.fy}</td>
                                      <td className="px-5 py-3 text-gray-600">{row.period}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            {activeCategory === "clients" && (
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-xs text-gray-400 border-b border-gray-100">
                                    <th className="px-5 py-3 text-left font-medium">Sr.No</th>
                                    <th className="px-5 py-3 text-left font-medium">Client Name</th>
                                    <th className="px-5 py-3 text-left font-medium">Category</th>
                                    <th className="px-5 py-3 text-left font-medium">Contact Person</th>
                                    <th className="px-5 py-3 text-left font-medium">Email</th>
                                    <th className="px-5 py-3 text-left font-medium">Phone</th>
                                    <th className="px-5 py-3 text-left font-medium">Country</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {uploadedData.filter(r => r.status !== "duplicate").slice(0, 8).map((row, idx) => (
                                    <tr key={row.id} className="border-b border-gray-50 hover:bg-gray-50">
                                      <td className="px-5 py-3 text-gray-500">{idx + 1}</td>
                                      <td className="px-5 py-3">
                                        <span className="px-2 py-0.5 bg-pink-100 text-pink-700 rounded text-xs">{row.companyName}</span>
                                      </td>
                                      <td className="px-5 py-3">
                                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">{row.category || "Standard"}</span>
                                      </td>
                                      <td className="px-5 py-3">
                                        <span className="text-[#1d6fa4] text-xs">{row.contactPerson}</span>
                                      </td>
                                      <td className="px-5 py-3">
                                        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">{row.email}</span>
                                      </td>
                                      <td className="px-5 py-3">
                                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{row.phone}</span>
                                      </td>
                                      <td className="px-5 py-3 text-gray-600">{row.country}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            {activeCategory === "events" && (
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-xs text-gray-400 border-b border-gray-100">
                                    <th className="px-5 py-3 text-left font-medium">Sr.No</th>
                                    <th className="px-5 py-3 text-left font-medium">Title</th>
                                    <th className="px-5 py-3 text-left font-medium">Start Date</th>
                                    <th className="px-5 py-3 text-left font-medium">End Date</th>
                                    <th className="px-5 py-3 text-left font-medium">Type</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {uploadedData.filter(r => r.status !== "duplicate").slice(0, 8).map((row, idx) => (
                                    <tr key={row.id} className="border-b border-gray-50 hover:bg-gray-50">
                                      <td className="px-5 py-3 text-gray-500">{idx + 1}</td>
                                      <td className="px-5 py-3">
                                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">{row.title}</span>
                                      </td>
                                      <td className="px-5 py-3 text-gray-600">{row.startDate}</td>
                                      <td className="px-5 py-3 text-gray-600">{row.endDate}</td>
                                      <td className="px-5 py-3">
                                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{row.type}</span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-3 mt-6">
                          <button
                            onClick={() => setCurrentStep(2)}
                            className="px-5 py-2 border border-gray-300 text-gray-600 text-sm rounded hover:border-[#093a5b] transition-all"
                          >
                            Back
                          </button>
                          <button
                            onClick={handleReset}
                            className="px-5 py-2 border border-gray-300 text-gray-600 text-sm rounded hover:border-gray-400 transition-all"
                          >
                            Start Over
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}

                </AnimatePresence>
              </div>
            </motion.div>
          </div>

          {/* Confetti */}
          <AnimatePresence>
            {showConfetti && (
              <>
                {[...Array(60)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ x: "50vw", y: "50vh", opacity: 1, scale: 1 }}
                    animate={{ x: `${Math.random() * 100}vw`, y: `${Math.random() * 100}vh`, opacity: 0, scale: 0, rotate: Math.random() * 720 }}
                    transition={{ duration: 2, ease: "easeOut" }}
                    className="fixed w-3 h-3 rounded-full pointer-events-none"
                    style={{ background: ["#093a5b","#1d6fa4","#0ea5e9","#10b981","#f59e0b"][i % 5], zIndex: 9999 }}
                  />
                ))}
              </>
            )}
          </AnimatePresence>

          {/* Success Toast */}
          <AnimatePresence>
            {submitSuccess && (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}
                className="fixed bottom-8 right-8 z-[10000]"
              >
                <div className="bg-[#093a5b] text-white px-6 py-4 rounded-xl shadow-xl flex items-center gap-3 max-w-sm">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-sm">Upload Successful!</div>
                    <div className="text-xs text-white/80 mt-0.5">
                      {validationStats.validated} {activeCategory} added to your system.
                    </div>
                  </div>
                  <button onClick={() => setSubmitSuccess(false)} className="ml-auto p-1 hover:bg-white/10 rounded">
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>



        {/* Integration Flow Section - Where Data Appears */}
        {integrationView && (
          <section
            id="integration-section"
            className="relative bg-gray-50 py-32 px-6 overflow-hidden"
          >
            <div className="max-w-7xl mx-auto relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-20"
              >
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 text-emerald-400 px-6 py-2 rounded-full mb-8">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">Successfully Uploaded</span>
                </div>
                <h2 className="Bulk_Action_DataLive ">
                  Your Data is{" "}
                  <span
                    className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent"
                    style={{ fontWeight: 400 }}
                  >
                    Live
                  </span>
                </h2>
                <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                  All {validationStats.validated} {activeCategory} are now
                  available across your workspace.
                </p>
              </motion.div>

              {/* Integration Journey */}
              <div className="grid md:grid-cols-4 gap-8 mb-16">
                {[
                  {
                    icon: Upload,
                    label: "Uploaded",
                    desc: `${validationStats.totalUploaded} records`,
                    color: "cyan",
                  },
                  {
                    icon: Brain,
                    label: "AI Validated",
                    desc: "100% accuracy",
                    color: "purple",
                  },
                  {
                    icon: Database,
                    label: "Integrated",
                    desc: "Into system",
                    color: "emerald",
                  },
                  {
                    icon: CheckCircle,
                    label: "Ready to Use",
                    desc: "Instantly available",
                    color: "green",
                  },
                ].map((step, idx) => {
                  const Icon = step.icon;
                  return (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="relative"
                    >
                      <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center shadow-sm">
                        <div
                          className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-${step.color}-500 to-${step.color}-600 flex items-center justify-center`}
                        >
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <h4 className="text-[#093a5b] font-semibold mb-2">{step.label}</h4>
                        <p className="text-gray-500 text-sm">{step.desc}</p>
                      </div>
                      {idx < 3 && (
                        <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-white/20 to-transparent" />
                      )}
                    </motion.div>
                  );
                })}
              </div>

              {/* Where to Find Data */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-gray-200 rounded-3xl p-12 mb-8 shadow-sm"
              >
                <h3 className="text-2xl text-[#093a5b] font-semibold mb-8 text-center">
                  Find Your Data Here
                </h3>
                <div className="grid md:grid-cols-3 gap-6">
                  {activeCategory === "tasks" && (
                    <>
                      <div className="p-6 bg-gray-50 border border-gray-200 rounded-2xl">
                        <ClipboardList className="w-8 h-8 text-emerald-400 mb-4" />
                        <h4 className="text-[#093a5b] font-semibold mb-2">Task Management</h4>
                        <p className="text-gray-500 text-sm">
                          All tasks are now visible in your task lists and
                          boards
                        </p>
                      </div>
                      <div className="p-6 bg-gray-50 border border-gray-200 rounded-2xl">
                        <LayoutDashboard className="w-8 h-8 text-emerald-400 mb-4" />
                        <h4 className="text-[#093a5b] font-semibold mb-2">Dashboards</h4>
                        <p className="text-gray-500 text-sm">
                          Task widgets updated with new data
                        </p>
                      </div>
                      <div className="p-6 bg-gray-50 border border-gray-200 rounded-2xl">
                        <BarChart3 className="w-8 h-8 text-emerald-400 mb-4" />
                        <h4 className="text-[#093a5b] font-semibold mb-2">Reports</h4>
                        <p className="text-gray-500 text-sm">
                          Analytics and insights reflect new tasks
                        </p>
                      </div>
                    </>
                  )}
                  {activeCategory === "clients" && (
                    <>
                      <div className="p-6 bg-gray-50 border border-gray-200 rounded-2xl">
                        <Users className="w-8 h-8 text-cyan-400 mb-4" />
                        <h4 className="text-[#093a5b] font-semibold mb-2">Client Directory</h4>
                        <p className="text-gray-500 text-sm">
                          All clients searchable and accessible
                        </p>
                      </div>
                      <div className="p-6 bg-gray-50 border border-gray-200 rounded-2xl">
                        <Building className="w-8 h-8 text-cyan-400 mb-4" />
                        <h4 className="text-[#093a5b] font-semibold mb-2">Projects</h4>
                        <p className="text-gray-500 text-sm">
                          Ready to assign to projects
                        </p>
                      </div>
                      <div className="p-6 bg-gray-50 border border-gray-200 rounded-2xl">
                        <FileText className="w-8 h-8 text-cyan-400 mb-4" />
                        <h4 className="text-[#093a5b] font-semibold mb-2">Documents</h4>
                        <p className="text-gray-500 text-sm">
                          Client folders created automatically
                        </p>
                      </div>
                    </>
                  )}
                  {activeCategory === "events" && (
                    <>
                      <div className="p-6 bg-gray-50 border border-gray-200 rounded-2xl">
                        <Calendar className="w-8 h-8 text-purple-400 mb-4" />
                        <h4 className="text-[#093a5b] font-semibold mb-2">Calendar</h4>
                        <p className="text-gray-500 text-sm">
                          All events visible on calendar views
                        </p>
                      </div>
                      <div className="p-6 bg-gray-50 border border-gray-200 rounded-2xl">
                        <Clock className="w-8 h-8 text-purple-400 mb-4" />
                        <h4 className="text-[#093a5b] font-semibold mb-2">Reminders</h4>
                        <p className="text-gray-500 text-sm">
                          Notifications set for upcoming events
                        </p>
                      </div>
                      <div className="p-6 bg-gray-50 border border-gray-200 rounded-2xl">
                        <Users className="w-8 h-8 text-purple-400 mb-4" />
                        <h4 className="text-[#093a5b] font-semibold mb-2">Team Schedules</h4>
                        <p className="text-gray-500 text-sm">
                          Attendees notified automatically
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </motion.div>

              {/* Try Again Button */}
              <div className="text-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleReset}
                  className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-emerald-500 text-white rounded-xl shadow-lg hover:shadow-2xl transition-all"
                >
                  Upload More Data
                </motion.button>
              </div>
            </div>
          </section>
        )}

        {/* AI Intelligence Section */}
        <section className="relative bg-gradient-to-b from-gray-50 to-white py-32 px-6 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-50 via-purple-50/30 to-white" />

          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-20"
            >
              {/* <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 text-purple-600 px-6 py-2 rounded-full mb-8">
                <Brain className="w-4 h-4" />
                <span className="text-sm">AI Intelligence</span>
              </div> */}
              <h2 className="Bulk_Action_DataLive">
                Smart AI{" "}
                {/* bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent */}
                <span className="text-[#093a5b]">Behind Every Upload</span>
              </h2>
              {/* text-xl text-gray-600 max-w-3xl mx-auto */}
              <p className=" Advanced_machine_learning">
                Advanced machine learning works in milliseconds to ensure
                perfect data quality.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: Cpu,
                  title: "Smart Duplicate Detection",
                  description:
                    "AI compares every record against your entire database to find exact matches and similar items.",
                  color: "orange",
                  stat: "99.9%",
                  statLabel: "Accuracy",
                },
                {
                  icon: Activity,
                  title: "Real-time Validation",
                  description:
                    "Instant format checking, required field validation, and data type verification.",
                  color: "cyan",
                  stat: "<1s",
                  statLabel: "Per 100 records",
                },
                {
                  icon: ShieldCheck,
                  title: "Auto-Classification",
                  description:
                    "Automatically categorizes and tags data based on content, context, and patterns.",
                  color: "emerald",
                  stat: "100%",
                  statLabel: "Automated",
                },
                {
                  icon: Link2,
                  title: "Smart Matching",
                  description:
                    "Identifies relationships and suggests matches with existing records in your system.",
                  color: "purple",
                  stat: "85%",
                  statLabel: "Match rate",
                },
              ].map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    whileHover={{ y: -10 }}
                    className="bg-white backdrop-blur-xl border border-gray-200 rounded-2xl p-8 hover:border-gray-300 hover:shadow-xl transition-all"
                  >
                    <div
                      className={`w-16 h-16 rounded-2xl bg-gradient-to-br from-${feature.color}-500 to-${feature.color}-600 flex items-center justify-center mb-6`}
                    >
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 mb-6 leading-relaxed text-sm">
                      {feature.description}
                    </p>
                    <div
                      className={`p-4 bg-${feature.color}-500/10 rounded-xl border border-${feature.color}-500/20`}
                    >
                      <div
                        className={`text-2xl text-${feature.color}-400 mb-1`}
                        style={{ fontWeight: 200 }}
                      >
                        {feature.stat}
                      </div>
                      <div className="text-xs text-gray-500">
                        {feature.statLabel}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Trust & Security Section */}
        {/* <section className="relative bg-gradient-to-b from-black to-gray-900 py-32 px-6 overflow-hidden">
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-20"
            >
              <h2
                className="text-5xl md:text-6xl text-white mb-6"
                style={{ fontWeight: 300 }}
              >
                Enterprise-Grade{" "}
                <span
                  className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent"
                  style={{ fontWeight: 400 }}
                >
                  Security
                </span>
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Your data is protected at every step of the upload process.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Lock,
                  title: "Encrypted Uploads",
                  description:
                    "All files encrypted during transfer with industry-standard SSL/TLS protocols.",
                },
                {
                  icon: Shield,
                  title: "Data Validation",
                  description:
                    "Multi-layer validation ensures only clean, accurate data enters your system.",
                },
                {
                  icon: Database,
                  title: "Backup & Recovery",
                  description:
                    "Automatic backups before any bulk operation with instant rollback capability.",
                },
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 rounded-2xl p-8 text-center group hover:border-white/20 transition-all"
                  >
                    <motion.div
                      className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center relative"
                      whileHover={{ rotate: [0, -5, 5, -5, 0], scale: 1.1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Icon className="w-10 h-10 text-white" />
                      <motion.div
                        animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="absolute inset-0 bg-cyan-400/30 rounded-2xl blur-lg"
                      />
                    </motion.div>
                    <h3 className="text-xl text-white mb-3">{item.title}</h3>
                    <p className="text-gray-400">{item.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section> */}

        {/* Final CTA */}
        <section className="relative bg-gradient-to-br from-white via-gray-50 to-white py-32 px-6 overflow-hidden">
          {/* <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(6,182,212,0.05),transparent_50%)]" /> */}

          <div className="max-w-4xl mx-auto relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="Bulk_Action_Stop">
                Stop Wasting Time on <br />
                {/* bg-gradient-to-r from-cyan-600 to-emerald-600 bg-clip-text text-transparent */}
                <span className="text-[#093a5b]">Manual Entry</span>
              </h2>
              <p className="Bulk_Action_Stop_para">
                Upload 1,000 records under 30 seconds. Let Simplifyte
                Intelligence handle the validation. Focus on what matters.
              </p>

              {/* Social Proof */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="flex items-center justify-center gap-2 mb-8"
              >
                <div className="flex -space-x-2">
                  <div className="flex -space-x-2">
                    {Object.values(ImageData).map((img, i) => (
                      <div
                        key={i}
                        className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-purple-500 border-2 border-white"
                        // style={{ backgroundImage: `url(${img})` }}
                      >
                        <img src={img} className="rounded-full" />
                      </div>
                    ))}
                  </div>
                </div>
                <span className="text-sm text-gray-600 ml-2">
                  Join 10,000+ teams uploading faster
                </span>
              </motion.div>

              <div className="flex flex-row sm:flex-row items-center justify-center gap-4 mb-8">
                <motion.button
                  whileHover={
                    {
                      // scale: 1.05,
                      // boxShadow: "0 20px 40px rgba(6, 182, 212, 0.4)",
                    }
                  }
                  // whileTap={{ scale: 0.95 }}
                  //  bg-gradient-to-r from-cyan-500 to-emerald-500
                  className="px-12 py-5 bg-[#093A5b] class_white rounded-xl text-base shadow-2xl shadow-cyan-500/50 relative 
                  overflow-hidden hover:bg-white font-semibold duration-500 group"
                  onClick={() => {
                    window.location.href = "https://app.simplifyte.com/login";
                  }}
                >
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    animate={{ x: ["-100%", "200%"] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatDelay: 1,
                    }}
                  />
                  <span className="relative z-10 flex items-center gap-2">
                    Start Free Trial
                    <ArrowRight className="w-5 h-5" />
                  </span>
                </motion.button>

                <motion.button
                  className="px-12 py-5 rounded-xl text-base font-semibold hover:border-gray-400 watch_video_class duration-500 transition-all border border-[#093a5b]"
                  onClick={() =>
                    (window.location.href =
                      "https://simplifyte.com/docs/bulk-uploads")
                  }
                >
                  Watch 2-Min Demo
                </motion.button>
              </div>

              {/* Quick Benefits */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600"
              >
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#093a5b]" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#093a5b]" />
                  <span>30 day free trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#093a5b]" />
                  <span>Setup in 60 seconds</span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>
      <CustomFooter />
    </>
  );
}
